import { useState, useEffect } from 'react';

/**
 * Global API Error Banner - shows when backend is unreachable
 * Listens to custom events dispatched by api.js interceptor
 */
function ApiErrorBanner() {
    const [error, setError] = useState(null);
    const [isRetrying, setIsRetrying] = useState(false);

    useEffect(() => {
        const handleApiError = (event) => {
            setError(event.detail);
        };

        const handleApiRecovered = () => {
            setError(null);
        };

        window.addEventListener('api:error', handleApiError);
        window.addEventListener('api:recovered', handleApiRecovered);

        return () => {
            window.removeEventListener('api:error', handleApiError);
            window.removeEventListener('api:recovered', handleApiRecovered);
        };
    }, []);

    const handleRetry = async () => {
        setIsRetrying(true);
        try {
            // Simple health check
            const response = await fetch('/api/health');
            if (response.ok) {
                setError(null);
                window.dispatchEvent(new CustomEvent('api:recovered'));
                window.location.reload();
            }
        } catch (e) {
            // Still failing
        }
        setIsRetrying(false);
    };

    if (!error) return null;

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 9999,
            background: error.type === 'network'
                ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
                : 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
            color: '#ffffff',
            padding: '12px 20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '16px',
            flexWrap: 'wrap',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
        }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontSize: '1.2rem' }}>
                    {error.type === 'network' ? '!' : '!'}
                </span>
                <span style={{ fontWeight: 600 }}>
                    {error.type === 'network'
                        ? 'Unable to connect to server'
                        : error.type === '502'
                            ? 'Server is temporarily unavailable'
                            : 'Server error occurred'}
                </span>
            </div>
            <p style={{
                margin: 0,
                fontSize: '0.875rem',
                opacity: 0.9,
                maxWidth: '400px',
            }}>
                {error.message || 'Please check your connection and try again.'}
            </p>
            <button
                onClick={handleRetry}
                disabled={isRetrying}
                style={{
                    padding: '8px 16px',
                    borderRadius: '8px',
                    border: '2px solid rgba(255, 255, 255, 0.4)',
                    background: 'rgba(255, 255, 255, 0.15)',
                    color: '#ffffff',
                    fontWeight: 600,
                    fontSize: '0.85rem',
                    cursor: isRetrying ? 'not-allowed' : 'pointer',
                    opacity: isRetrying ? 0.7 : 1,
                }}
            >
                {isRetrying ? 'Checking...' : 'Retry'}
            </button>
            <button
                onClick={() => setError(null)}
                style={{
                    padding: '6px 12px',
                    borderRadius: '6px',
                    border: 'none',
                    background: 'transparent',
                    color: 'rgba(255, 255, 255, 0.8)',
                    fontSize: '1.2rem',
                    cursor: 'pointer',
                    lineHeight: 1,
                }}
                title="Dismiss"
            >
                x
            </button>
        </div>
    );
}

export default ApiErrorBanner;
